// utils/conversationMarginDots.ts
//
// Where a book's conversations sit in the reading view's right margin: one
// dot per line of text that a conversation was anchored to. Pure functions;
// the component measures the DOM and hands the rectangles in.

import { ChatSessionSummary } from '@/types/chat';
import {
  ChapterRuntime,
  MountedChapterIndex,
  getSentenceFingerprint,
  resolveTimingOrdinal,
} from '@/utils/ordinalSync';

/** Line centres this close together are the same line of text. */
export const SAME_LINE_TOLERANCE_PX = 4;
/** The dot's tap target, before clipping between near neighbours. */
export const MARGIN_DOT_HIT_TARGET_PX = 44;

/**
 * The anchor fields a dot is placed from. The list endpoint sends null for
 * a voice conversation that began with the player not ready, and the text
 * sheet sends -1; neither resolves to a line.
 */
export type MarginAnchor = Pick<ChatSessionSummary, 'anchor_time_offset' | 'anchor_text_fingerprint'> & {
  anchor_sentence_index: number | null | undefined;
};

export interface MarginLinePoint {
  session: ChatSessionSummary;
  /** The line's vertical centre, in px from the top of the measured wrapper. */
  top: number;
}

export interface MarginDotPlacement {
  /** The dot's centre, in px from the top of the wrapper. */
  top: number;
  /** The tap target, in px from the top of the wrapper; clipped so that
   *  dots on adjacent lines never overlap. */
  hitTop: number;
  hitBottom: number;
  /** Newest first. A tap opens the first. */
  sessions: ChatSessionSummary[];
}

/**
 * The token ordinal a conversation's anchor names, or -1.
 *
 * The time offset is the precise coordinate (a word), but timings are per
 * reading voice, so an offset recorded under another voice lands on another
 * word. The fingerprint is voice-independent: when the sentence at the
 * resolved word is not the sentence the anchor was taken in, the offset has
 * drifted, and the sentence index (a paragraph, as the publisher emits one
 * range per block) places the dot on the paragraph's first line instead.
 */
export const resolveAnchorOrdinal = (anchor: MarginAnchor, runtime: ChapterRuntime): number => {
  const { tokenMap, timings } = runtime;
  const seconds = anchor.anchor_time_offset;
  if (seconds != null && seconds >= 0) {
    const ordinal = resolveTimingOrdinal(timings.startsMs, timings.endsMs, seconds * 1000);
    const fingerprint = anchor.anchor_text_fingerprint;
    if (ordinal >= 0 && (!fingerprint || getSentenceFingerprint(tokenMap, ordinal) === fingerprint)) {
      return ordinal;
    }
  }
  const sentenceIndex = anchor.anchor_sentence_index;
  const sentence = sentenceIndex != null && sentenceIndex >= 0 ? tokenMap.sentences[sentenceIndex] : undefined;
  return sentence ? sentence[0] : -1;
};

const anchorElement = (index: MountedChapterIndex, ordinal: number): HTMLElement | null =>
  index.segmentsByOrdinal[ordinal]?.[0] ?? index.blockElementByOrdinal[ordinal] ?? null;

// The first client rect is the first line box, which is the right one for a
// word the browser split across two lines. An element that isn't laid out
// (display: none, or detached) reports no height and gets no dot.
const firstLineRect = (element: Element): DOMRect | null => {
  const rects = element.getClientRects();
  const rect = rects.length > 0 ? rects[0] : element.getBoundingClientRect();
  return rect.height > 0 ? rect : null;
};

/**
 * The line each conversation sits on, for one mounted chapter. Conversations
 * whose anchor resolves to nothing, or to an element that isn't laid out,
 * are left out.
 */
export const measureConversationLines = (
  sessions: readonly ChatSessionSummary[],
  runtime: ChapterRuntime,
  index: MountedChapterIndex,
  wrapperTop: number,
): MarginLinePoint[] => {
  const points: MarginLinePoint[] = [];
  for (const session of sessions) {
    const ordinal = resolveAnchorOrdinal(session, runtime);
    if (ordinal < 0) continue;
    const element = anchorElement(index, ordinal);
    const rect = element ? firstLineRect(element) : null;
    if (!rect) continue;
    points.push({ session, top: rect.top - wrapperTop + rect.height / 2 });
  }
  return points;
};

const newestFirst = (a: ChatSessionSummary, b: ChatSessionSummary): number => {
  const byTime = Date.parse(b.last_message_at) - Date.parse(a.last_message_at);
  return Number.isNaN(byTime) || byTime === 0 ? b.id - a.id : byTime;
};

/**
 * One dot per line, across every measured chapter. Lines within
 * SAME_LINE_TOLERANCE_PX share a dot, newest conversation first. Adjacent
 * lines are closer than the tap target is tall, so neighbouring targets are
 * clipped to the midpoint between them.
 */
export const layoutMarginDots = (points: readonly MarginLinePoint[]): MarginDotPlacement[] => {
  const half = MARGIN_DOT_HIT_TARGET_PX / 2;
  const placements: MarginDotPlacement[] = [];
  for (const point of [...points].sort((a, b) => a.top - b.top)) {
    const last = placements[placements.length - 1];
    if (last && point.top - last.top <= SAME_LINE_TOLERANCE_PX) {
      last.sessions.push(point.session);
      continue;
    }
    placements.push({
      top: point.top,
      hitTop: point.top - half,
      hitBottom: point.top + half,
      sessions: [point.session],
    });
  }
  for (const placement of placements) {
    placement.sessions.sort(newestFirst);
  }
  for (let i = 1; i < placements.length; i += 1) {
    const above = placements[i - 1];
    const below = placements[i];
    if (below.top - above.top < MARGIN_DOT_HIT_TARGET_PX) {
      const midpoint = (above.top + below.top) / 2;
      above.hitBottom = Math.min(above.hitBottom, midpoint);
      below.hitTop = Math.max(below.hitTop, midpoint);
    }
  }
  return placements;
};

/** The dots for one chapter: its conversations, measured and laid out. */
export const placeMarginDots = (
  sessions: readonly ChatSessionSummary[],
  chapterId: string,
  runtime: ChapterRuntime,
  index: MountedChapterIndex,
  wrapperTop: number,
): MarginDotPlacement[] => layoutMarginDots(measureConversationLines(
  sessions.filter((session) => session.anchor_chapter_id === chapterId),
  runtime,
  index,
  wrapperTop,
));
